import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, ArrowUpRight, Loader2, Sparkles, Lock, ArrowRight } from "lucide-react";

/**
 * PjpWalletCard — in-house wallet balance + last 5 transactions.
 * Cash-out is deferred (Stripe Connect / verified ACH will land in a future
 * iteration). Members can express interest via the "Notify me" button below.
 */
export default function PjpWalletCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [interested, setInterested] = useState(false);

  useEffect(() => {
    api.get("/wallet/me").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);

  const notifyMe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/wallet/cashout-interest");
      setInterested(true);
      toast.success(r.data.message);
    } catch { toast.error("Couldn't save your interest — try again"); }
    finally { setBusy(false); }
  };

  if (!data) {
    return (
      <div className="card !p-5 flex items-center justify-center min-h-[180px]" data-testid="pjp-wallet-loading">
        <Loader2 className="h-4 w-4 animate-spin text-cyan" />
      </div>
    );
  }
  if (data.error) return null;

  const cents = data.wallet?.balance_cents || 0;
  const balance = (cents / 100).toLocaleString(undefined, { style: "currency", currency: data.wallet?.currency || "USD" });
  const txs = data.transactions || [];

  return (
    <div
      className="card !p-0 overflow-hidden relative"
      data-testid="pjp-wallet-card"
      style={{ background: "linear-gradient(155deg, rgba(75,156,255,0.10) 0%, rgba(5,8,16,0.85) 55%)" }}
    >
      {/* Header — balance */}
      <div className="p-5 border-b border-white/[0.06] relative">
        <div className="flex items-center justify-between">
          <Link
            to="/wallet"
            data-testid="wallet-header-link"
            className="flex items-center gap-2 hover:opacity-90 transition-opacity"
            title="Open full Wallet page"
          >
            <Wallet className="h-4 w-4 text-cyan" />
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// PJP Wallet</div>
          </Link>
          <span className="mono text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 flex items-center gap-1">
            <Lock className="h-2.5 w-2.5" /> Cash-out soon
          </span>
        </div>
        <div className="mt-3 flex items-end gap-2">
          <div className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white leading-none" data-testid="wallet-balance">{balance}</div>
          <div className="mono text-[11px] text-white/40 pb-1.5">USD</div>
        </div>
        <div className="text-[11px] text-white/50 mt-2">
          In-house digital wallet. Earn credits from bonuses, referrals, and empty-leg fills. Withdraw to your bank once we finish Stripe Connect + ACH verification.
        </div>
      </div>

      {/* Transactions */}
      <div className="p-5 pt-4">
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-white/45 mb-2">Recent ledger</div>
        {txs.length === 0 ? (
          <div className="text-sm text-white/45 py-3">No activity yet. Refer a pilot and earn <span className="text-cyan">$50</span> in credit.</div>
        ) : (
          <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1" data-testid="wallet-tx-list">
            {txs.slice(0, 6).map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-white/[0.04] last:border-b-0" data-testid={`wallet-tx-${t.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="p-1 rounded bg-white/[0.03] border border-white/[0.06]">
                    <Sparkles className={`h-3 w-3 ${t.amount_cents >= 0 ? "text-avail" : "text-red-400"}`} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[12px] text-white/85 truncate">{t.description}</div>
                    <div className="mono text-[9px] tracking-widest uppercase text-white/35">{new Date(t.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className={`mono text-sm font-semibold ${t.amount_cents >= 0 ? "text-avail" : "text-red-300"}`}>
                  {t.amount_cents >= 0 ? "+" : "−"}${Math.abs(t.amount_cents / 100).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer — cash-out cta + full wallet page CTA */}
      <div className="p-4 border-t border-white/[0.06] flex items-center justify-between gap-3 flex-wrap">
        <Link
          to="/wallet"
          data-testid="wallet-open-full"
          className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded bg-cyan text-obsidian font-bold hover:brightness-110 flex items-center gap-1.5 flex-shrink-0"
        >
          Open Wallet <ArrowRight className="h-3 w-3" />
        </Link>
        <button
          type="button"
          onClick={notifyMe}
          disabled={busy || interested}
          data-testid="wallet-cashout-notify"
          className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-cyan/40 text-cyan hover:bg-cyan/10 disabled:opacity-60 flex items-center gap-1.5 flex-shrink-0"
        >
          {interested ? "You're on the list ✓" : (
            <>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowUpRight className="h-3 w-3" />} Notify · cash-out</>
          )}
        </button>
      </div>
    </div>
  );
}
